'use client';

import { useState, useEffect } from 'react';
import { MiniChatHeader } from './MiniChatHeader';
import { MiniChatList } from './MiniChatList';
import { MiniChatMessages } from './MiniChatMessages';
import { cn } from '@/src/shared/lib/utils';
import { useChatStore } from '../model/useChatStore';
import { useAuthStore } from '@/src/shared/lib/store/auth-store';

type WidgetState = 'collapsed' | 'chatsList' | 'activeChat';

interface MiniChatWidgetProps {
  className?: string;
  /** Control visibility externally */
  isOpen?: boolean;
  /** Callback when widget is closed */
  onClose?: () => void;
  /** Callback when extend button is clicked, receives conversationId if chat is open */
  onExtend?: (conversationId: string | null) => void;
  /** Initial state when opened */
  initialState?: 'collapsed' | 'chatsList';
}

/**
 * Mini Chat Widget - Floating chat widget in bottom-right corner
 *
 * States:
 * - collapsed: Only header visible (80px height)
 * - chatsList: Full widget with list of chats (550px height)
 * - activeChat: Full widget with selected chat conversation (550px height)
 */
export const MiniChatWidget = ({
  className,
  isOpen = true,
  onClose,
  onExtend,
  initialState = 'chatsList',
}: MiniChatWidgetProps) => {
  const [widgetState, setWidgetState] = useState<WidgetState>(initialState);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Reset to initial state when opened
  useEffect(() => {
    if (isOpen) {
      setWidgetState(initialState);
    }
  }, [isOpen, initialState]);

  const { conversations, connectionStatus } = useChatStore();

  // Calculate total unread count
  const totalUnreadCount = conversations.reduce(
    (sum, conv) => sum + (conv.unreadCount || 0),
    0
  );

  // Get selected conversation name
  const selectedConversation = conversations.find(
    (c) => c.id === selectedConversationId
  );
  const selectedChatName =
    selectedConversation?.otherParticipant?.displayName ||
    selectedConversation?.otherParticipant?.name ||
    'Chat';

  // Handle expand from collapsed
  const handleExpand = () => {
    if (widgetState === 'collapsed') {
      setWidgetState('chatsList');
    }
  };

  // Handle collapse - closes the mini chat widget entirely
  const handleCollapse = () => {
    onClose?.();
  };

  // Handle extend to full page
  const handleExtend = () => {
    onExtend?.(selectedConversationId);
  };

  // Handle select chat
  const handleSelectChat = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setWidgetState('activeChat');
  };

  // Handle back to chat list
  const handleBackToList = () => {
    setSelectedConversationId(null);
    setWidgetState('chatsList');
  };

  // Don't render if not authenticated or not open
  if (!isAuthenticated || !isOpen) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed bottom-5 right-5 z-50',
        'transition-all duration-300 ease-in-out',
        className
      )}
    >
      <div
        className={cn(
          'w-[500px] bg-background-primary rounded-[30px] overflow-hidden',
          'shadow-[0_4px_30px_rgba(0,0,0,0.15)]',
          'flex flex-col',
          widgetState === 'collapsed' ? 'h-20' : 'h-[550px]',
          widgetState !== 'collapsed' && 'outline outline-1 outline-accent-secondary'
        )}
      >
        {/* Header */}
        <MiniChatHeader
          state={widgetState}
          title={widgetState === 'activeChat' ? selectedChatName : 'Chats'}
          unreadCount={totalUnreadCount}
          onExpand={handleExpand}
          onCollapse={handleCollapse}
          onExtend={handleExtend}
          onBack={widgetState === 'activeChat' ? handleBackToList : undefined}
          connectionStatus={connectionStatus}
        />

        {/* Content */}
        {widgetState === 'chatsList' && (
          <MiniChatList
            selectedConversationId={selectedConversationId}
            onSelectConversation={handleSelectChat}
          />
        )}

        {widgetState === 'activeChat' && selectedConversationId && (
          <MiniChatMessages conversationId={selectedConversationId} />
        )}
      </div>
    </div>
  );
};
